import React, { useState } from "react"
import { Benefit } from "#/lib/carrot-api/types/Benefit"
import { Provider } from "#/pages/provider-finder/types/Providers"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { getPiiProps } from "#/services/tracking"
import { Box, Button, Link, Typography } from "@carrotfertility/carotene-core"
import { FormattedMessage } from "react-intl"
import { AccessOriginAccountDialog } from "#/components/telemedicine/pelvic-floor-therapy/AccessOriginAccountDialog"
import { DirectionsLink, ProviderPhone } from "#/pages/provider-finder/components/results/ProviderDetailsMainContent"
import { VirtualServiceRestrictionNotice } from "#/pages/provider-finder/components/results/VirtualServiceRestrictionNotice"
import { ProviderChips } from "#/pages/provider-finder/components/results/ProviderChips"
import { Paths } from "#/utils/Paths"

function OriginLearnMoreLink({ providerUuid }: { providerUuid: number }): JSX.Element {
  const { mutate: trackMemberAction } = useMemberActionTracking()

  return (
    <Link
      data-id="origin-learn-more-link"
      href={Paths.TELEMEDICINE_FOR_PELVIC_FLOOR_THERAPY}
      target="_blank"
      rel="noreferrer noopener"
      color="inherit"
      onClick={() => {
        trackMemberAction({
          memberActionTaken: MemberActions.PROVIDER_CARD_INTERACTION_ORIGIN_LEARN_MORE,
          metaData: { providerUuid }
        })
      }}
    >
      <FormattedMessage defaultMessage="Learn more about Origin" />
    </Link>
  )
}

function GetStartedWithOriginButton({
  providerUuid,
  officeId
}: {
  providerUuid: number
  officeId?: number
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const { mutate: trackMemberAction } = useMemberActionTracking()

  const handleClick = () => {
    trackMemberAction({
      memberActionTaken: MemberActions.PROVIDER_CARD_INTERACTION_ORIGIN_GET_STARTED,
      metaData: { providerUuid, officeId }
    })
    setOpen(true)
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        color="secondary"
        onClick={handleClick}
        sx={{ inlineSize: "fit-content" }}
        data-id="origin-get-started-button"
      >
        <FormattedMessage defaultMessage="Get started with Origin" />
      </Button>
      <AccessOriginAccountDialog open={open} onClose={() => setOpen(false)} officeId={officeId} />
    </>
  )
}

type OriginProviderCardContentProps = {
  provider: Provider
  searchLocation: string
  benefit: Benefit
}

export function OriginProviderCardContent({
  provider,
  benefit,
  searchLocation
}: OriginProviderCardContentProps): JSX.Element {
  const { providerName, address, phone, providerUuid, virtualServicesNotAvailableIn, originOfficeId } = provider

  return (
    <Box
      {...getPiiProps()}
      gap={(theme) => theme.spacing(theme.tokens.spacing.md)}
      sx={{ maxInlineSize: "sm", display: "flex", flexDirection: "column" }}
    >
      <Typography variant="h4" component="h3" sx={(theme) => ({ color: theme.palette.text.secondary })}>
        {providerName}
      </Typography>
      {address && (
        <Typography sx={(theme) => ({ color: theme.palette.text.secondary, wordBreak: "break-word" })}>
          {address}
        </Typography>
      )}
      <Box
        gap={(theme) => `${theme.spacing(theme.tokens.spacing.xs)} ${theme.spacing(theme.tokens.spacing.lg)}`}
        sx={{ whiteSpace: "nowrap", flexWrap: "wrap", display: "flex", flexDirection: "row" }}
      >
        <OriginLearnMoreLink {...{ providerUuid }} />
        {phone && <ProviderPhone {...{ phone, providerUuid }} />}
        {address && <DirectionsLink {...{ address, searchLocation, providerUuid }} />}
      </Box>
      <ProviderChips {...{ provider, benefit }} />
      <VirtualServiceRestrictionNotice {...{ virtualServicesNotAvailableIn }} />
      <GetStartedWithOriginButton providerUuid={providerUuid} officeId={originOfficeId} />
    </Box>
  )
}
